import closeIcon from '@/shared/ui/icons/Components/Common/close-icon.svg';
import coloredArrowRightIcon from '@/shared/ui/icons/Components/colored-arrow-right-icon.svg';
import editIcon from '@/shared/ui/icons/Tables/colored-edit-icon.svg';
import { auth } from '@/shared/lib';
import { Drawer } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import { IconButton, NormalButton, StudyTypeFormatter, EditModal } from '@/shared/ui';
import useStudyInfoDrawer from './useStudyInfoDrawer';
import './studyInfoDrawer.s.scss';

export type StudyInfoDrawerProps = {
  visible: boolean;
  title: string;
  fetchStudyId: string;
  handleClickCloseButton: () => void;
};

const StudyInfoDrawer = ({
  visible,
  title,
  fetchStudyId,
  handleClickCloseButton,
}: StudyInfoDrawerProps) => {
  const {
    state,
    toggleDisplayEditModal,
    generateInputList,
    onChangeStudyNote,
    onClickSaveStudyNote,
    onClickCloseStudyNote,
    handleScroll,
  } = useStudyInfoDrawer({ visible, fetchStudyId, handleClickCloseButton });

  /**
   * Renders a study's total artifact percentage as a severity-coded badge. Yields nothing when the value is nil, and otherwise marks the figure as potentially lost from 70 percent up or as needing attention from 50 percent up, appending the matching label.
   *
   * @param percentage - Total artifact percentage for the study, or nil when it is unavailable.
   * @returns The rendered artifact percentage badge, or null when the percentage is nil.
   */
  const artifactPercentageRenderer = (percentage: number) => {
    if (_.isNil(percentage)) {
      return null;
    }

    const isPotentiallyLost = percentage >= 70;
    const isNeedAttention = percentage >= 50;
    return (
      <div
        className={classnames(
          'total-artifact-percentage',
          isPotentiallyLost ? '--potentially-lost' : isNeedAttention ? '--need-attention' : '',
        )}
      >
        <strong>{`${percentage}%`}</strong>
        {(isPotentiallyLost || isNeedAttention) && (
          <div className="label">{isPotentiallyLost ? 'Potentially lost' : 'Need attention'}</div>
        )}
      </div>
    );
  };

  return (
    <>
      <EditModal
        isOpen={state.isOpenEditModal}
        toggle={onClickCloseStudyNote}
        header="Study Notes"
        isTextareaInput
        inputList={generateInputList()}
        onClickSave={onClickSaveStudyNote}
        onChange={onChangeStudyNote}
        isSaving={state.isSaving}
        maxChar={400}
      />

      <Drawer
        className="custom-drawer study-info-drawer"
        width={450}
        closable={false}
        onClose={handleClickCloseButton}
        open={visible}
        aria-label={title}
      >
        <div
          className={classnames(
            'drawer-title-container',
            state.isScroll ? '--has-box-shadow' : '',
            state.isAbortedStudy ? '--aborted-study' : '',
          )}
        >
          <div className="container-top d-flex">
            <span className="drawer-title">{title}</span>
            <IconButton
              isFilled
              iconComponent={<img src={closeIcon} alt="Close icon" />}
              onClick={handleClickCloseButton}
            />
          </div>

          {state.isAbortedStudy && (
            <div className="abort-study-message">
              This study has been aborted and will not be counted toward your billing.
            </div>
          )}
        </div>

        <div
          className={classnames(
            'drawer-body-container',
            auth.isCallCenter() ? 'style-callcenter' : '',
            state.isAbortedStudy ? '--aborted-study' : '',
          )}
          onScroll={handleScroll}
          id="drawer-body-container"
        >
          {_.map(
            _.filter(state.data, (x) => !!x),
            (element: any, index: number) => {
              if (element.isBiofluxDirectLabel) {
                return (
                  element.isBiofluxDirect && (
                    <div className="biodirect-label" key={index}>
                      Biodirect service has been applied to this study
                    </div>
                  )
                );
              }

              if (element.isLinkNewTab) {
                return (
                  <div className="link-text-container" key={index}>
                    <NormalButton
                      className="link-normal-button"
                      contentClassName="justify-between"
                      buttonName={element.buttonName}
                      onClick={() => {
                        window.open(element.link);
                      }}
                      hasIcon
                      iconComponent={<img src={coloredArrowRightIcon} alt="Right arrow icon" />}
                    />
                  </div>
                );
              }

              if (element.header === 'Study Notes') {
                return (
                  <div key={index} className="drawer-body">
                    {!!element.header && (
                      <div className="drawer-header">
                        <span>{element.header}</span>

                        <div id="edit-btn-id">
                          <IconButton
                            iconComponent={<img src={editIcon} alt="Edit icon" />}
                            disabled={element.isNotEdit || state.isAbortedStudy}
                            onClick={toggleDisplayEditModal}
                          />

                          {state.isAbortedStudy && (
                            <UncontrolledTooltip
                              className="custom-tooltip"
                              target="edit-btn-id"
                              placement="bottom-end"
                              delay={{ show: 0, hide: 0 }}
                              hideArrow
                            >
                              The study has been aborted. You cannot perform any actions.
                            </UncontrolledTooltip>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="drawer-content">
                      <div className="notes">{element.content}</div>
                    </div>
                  </div>
                );
              }

              if (element.header === 'Physician amendments notes') {
                return (
                  <div key={index} className="drawer-body">
                    {!!element.header && (
                      <div className="drawer-header">
                        <span>{element.header}</span>
                      </div>
                    )}

                    <div className="drawer-content">
                      <div className="notes">{element.content}</div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={index} className="drawer-body">
                  {!!element.header && (
                    <div className="drawer-header">
                      <span>{element.header}</span>
                    </div>
                  )}

                  <div className="drawer-content">
                    {_.map(
                      _.filter(element.content, (x: any) => !_.isEmpty(x)),
                      (item: any, index: number) => (
                        <div
                          key={index}
                          className={classnames(
                            'drawer-item',
                            element.className || '',
                            item.isDisabled ? 'is-disabled' : '',
                          )}
                        >
                          <span>{`${item.label}:`}</span>

                          {item.label === 'Status' ? (
                            <div className="study-status-container">
                              <span>{item.value}</span>
                            </div>
                          ) : item.isArtifactPercentage ? (
                            artifactPercentageRenderer(item.value)
                          ) : (
                            <span>
                              <StudyTypeFormatter
                                studyType={item.value}
                                isFollowOnStudy={
                                  (item.linkedStudies && item.linkedStudies.length) ||
                                  item?.followOnStudy
                                }
                                isTextButton={item.isTextButton}
                                handleClick={item.handleClick}
                                isDisabled={item.isDisabled}
                              />
                            </span>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              );
            },
          )}

          <div className="additional-div" />
        </div>
      </Drawer>
    </>
  );
};

export default StudyInfoDrawer;
